import type {
  ShipmentRoute,
  ShipmentRouteStop,
} from '@/services/api/shipments';
import type { ShipmentStatus } from '@/services/api/status';

/**
 * Ops "Cancel stop" (PRD TN2.11, CONTRACT-4W-NEGATIVE-SCENARIO-P0 §4 / §7 /
 * §11) — the rules the detail panel and the modal share, kept pure so they are
 * tested without rendering.
 */

/**
 * Firebase Remote Config flag (boolean, default OFF) that shows the per-stop
 * Cancel stop action. It stays OFF until the 4W cohort runs the P0 driver app:
 * an older app maps an unknown stop status to PENDING, so a CANCELLED stop
 * would still look open to the driver (eng review D4, PLAN §7).
 */
export const CANCEL_STOP_FLAG = 'feature_ops_cancel_stop';

/** English Mithril copy; reason labels stay as the API returns them. */
export const CANCEL_STOP_COPY = {
  action: 'Cancel stop',
  title: 'Cancel stop',
  explanation: 'The driver is notified and the goods stay on the vehicle.',
  openedHint:
    "Driver already arrived · ask the driver to record 'Ditolak atau dibatalkan di lokasi' (handled as a full return per client rule)",
  returnToOriginHint: "Return-to-origin stop can't be cancelled",
  notBilled: 'Not billed',
  resolved:
    'This stop is already closed (completed, failed or cancelled). The details have been refreshed.',
  routeEnded: 'This route has already ended. The details have been refreshed.',
} as const;

/** What the detail panel shows for one stop. */
export type StopCancelAction =
  /** Enabled "Cancel stop". */
  | 'CANCEL'
  /** Disabled "Cancel stop" with the "driver already arrived" hint. */
  | 'OPENED'
  /** No action, hint "Return-to-origin stop can't be cancelled". */
  | 'RETURN_TO_ORIGIN'
  /** Nothing: terminal, unknown status, or the server says it can't. */
  | 'NONE';

/**
 * The server decides (`cancellable`, `opened`, `isReturnToOrigin`); the web
 * only picks the presentation. A stop that is not PENDING — terminal or an
 * unknown status — never gets an action.
 */
export const stopCancelAction = (
  stop: Pick<
    ShipmentRouteStop,
    'status' | 'cancellable' | 'opened' | 'isReturnToOrigin'
  >
): StopCancelAction => {
  if (stop.status !== 'PENDING') return 'NONE';
  if (stop.isReturnToOrigin) return 'RETURN_TO_ORIGIN';
  if (stop.opened) return 'OPENED';
  if (stop.cancellable) return 'CANCEL';
  return 'NONE';
};

/** `category = INTERNAL_ERROR` — a cancel caused by our own input mistake. */
export const NOT_BILLED_CATEGORY = 'INTERNAL_ERROR';

export const isNotBilled = (category: string | null | undefined): boolean =>
  category === NOT_BILLED_CATEGORY;

/**
 * Cancelling this stop would leave the route with nothing to deliver: every
 * other open stop is the return-to-origin leg (which Ops cannot cancel), or
 * this stop is the route's only PICKUP, whose cancel cascades to every drop
 * (a DIRECT_4W route serves one shipment).
 */
export const leavesEmptyTrip = (
  stops: Pick<ShipmentRouteStop, 'id' | 'status' | 'type' | 'isReturnToOrigin'>[],
  stopId: string
): boolean => {
  const target = stops.find((s) => s.id === stopId);
  if (!target || target.status !== 'PENDING') return false;
  const onlyPickup =
    target.type === 'PICKUP' &&
    stops.filter((s) => s.type === 'PICKUP').length === 1;
  const otherOpenWork = stops.filter(
    (s) => s.status === 'PENDING' && s.id !== stopId && !s.isReturnToOrigin
  );
  return onlyPickup || otherOpenWork.length === 0;
};

/**
 * E27 (eng review D3): a cancel that would leave an empty trip on a route that
 * has not started is offered as the existing Cancel shipment instead (it
 * cancels the DIRECT_4W route with it). Cancel shipment only works while the
 * shipment is CREATED; past that the per-stop cancel stays.
 */
export const offersCancelShipment = (
  shipmentStatus: ShipmentStatus | string,
  route: Pick<ShipmentRoute, 'startedAt'> & {
    stops: Pick<
      ShipmentRouteStop,
      'id' | 'status' | 'type' | 'isReturnToOrigin'
    >[];
  },
  stopId: string
): boolean =>
  shipmentStatus === 'CREATED' &&
  route.startedAt == null &&
  leavesEmptyTrip(route.stops, stopId);

/**
 * The message for a 409 that means the stop (or its route) moved on since the
 * panel loaded; the modal refreshes the detail and shows it. Null for every
 * other code — those show the server's own text.
 */
export const cancelStopConflictMessage = (
  code: string | undefined
): string | null => {
  switch (code) {
    case 'STOP_ALREADY_OPENED':
      return CANCEL_STOP_COPY.openedHint;
    case 'STOP_RESOLVED':
      return CANCEL_STOP_COPY.resolved;
    case 'ROUTE_ALREADY_COMPLETED':
      return CANCEL_STOP_COPY.routeEnded;
    default:
      return null;
  }
};
